import {productConfigIterator} from 'components/customer/Product/helpers/productConfigIterator';
import {Validation} from 'components/customer/Product/entity/Validation';
import {validator} from 'shared/validator';
import {object, ObjectSchema, string as yupString, number, array} from 'yup';
import {ProductForm} from 'components/customer/Product/entity/ProductForm';
import {Drawer} from 'components/customer/Product/entity/Drawer';
import {store} from 'store/customer/storeSetup';
import Excel from 'shared/Excel';
import {Structure} from 'components/customer/Product/entity/Structure';

export const getValidationData = () => {
    if (store) {
        const state = store.getState();
        const productState = state.productData;

        return {
            cabinet_is_rail_on_edge: productState.isRailOnEdge,
            cabinet_top_part: {
                widthFunction: productState.railWidth,
            },
            cabinet_carc_colour: {
                thickness: productState.carcaseThickness,
            },
        };
    }
};

const getInnerDrawerValidationRules = (
    validations: Validation[],
    schema: ObjectSchema<object, object, object, ''>
) => {
    const innerDrawerValidations = validations.filter((validation) => {
        return validation?.value?.includes('drawer_position_to_bottom');
    });

    if (schema && innerDrawerValidations?.length > 0) {
        return schema.shape({
            drawers: array().of(
                object({
                    position_to_bottom: number()
                        .required('Please enter drawer position')
                        .test(
                            'position',
                            'positions must not overlap',
                            (position, context) => {
                                const currentValues = context.from[1].value as {
                                    drawer_amount: number;
                                    drawers: Drawer[];
                                    cabinet_height: number;
                                };

                                let currentIndex = 0;
                                const drawerAmount = Number(
                                    currentValues.drawer_amount
                                );
                                const cabinetHeight = Number(
                                    currentValues.cabinet_height
                                );
                                const railInformation = getValidationData();

                                if (context?.path) {
                                    const regex = /\[(\d+)\]/g;
                                    const match = regex.exec(context.path);

                                    if (match) {
                                        currentIndex = Number(match[1]);
                                    }
                                }

                                if (currentIndex >= drawerAmount) return true;

                                const drawerTopClearance: number[] = [];
                                const drawerBottomClearance: number[] = [];
                                const drawerHeight: number[] = [];
                                const drawerPositionToBottom: number[] = [];
                                const drawerInnerBottomClearance: number[] = [];

                                currentValues.drawers.forEach(
                                    (drawer, index) => {
                                        if (index < drawerAmount) {
                                            drawerTopClearance.push(
                                                Number(
                                                    drawer.systemTopClearance ??
                                                        0
                                                )
                                            );
                                            drawerBottomClearance.push(
                                                Number(
                                                    drawer.systemBottomClearance ??
                                                        0
                                                )
                                            );
                                            drawerHeight.push(
                                                Number(
                                                    drawer.drawer_runner_height ??
                                                        0
                                                )
                                            );
                                            drawerPositionToBottom.push(
                                                Number(
                                                    drawer.position_to_bottom ??
                                                        0
                                                )
                                            );
                                            drawerInnerBottomClearance.push(
                                                Number(
                                                    drawer.systemInnerDrawerBottomClearance ??
                                                        0
                                                )
                                            );
                                        }
                                    }
                                );

                                const scope = {
                                    ...railInformation,
                                    index: currentIndex + 1,
                                    cabinet_height: cabinetHeight,
                                    drawer_top_clearance: drawerTopClearance,
                                    drawer_bottom_clearance:
                                        drawerBottomClearance,
                                    drawer_height: drawerHeight,
                                    drawer_position_to_bottom:
                                        drawerPositionToBottom,
                                    drawer_inner_bottom_clearance:
                                        drawerInnerBottomClearance,
                                };

                                try {
                                    const tests = innerDrawerValidations.map(
                                        (validation) => {
                                            let isEnabled = true;
                                            if (validation?.enabled) {
                                                if (
                                                    typeof validation.enabled ==
                                                    'boolean'
                                                ) {
                                                    isEnabled =
                                                        validation.enabled;
                                                } else if (
                                                    typeof validation.enabled ==
                                                    'string'
                                                ) {
                                                    const formula =
                                                        validation.enabled.replace(
                                                            'size',
                                                            'COUNT'
                                                        );

                                                    isEnabled = Excel.calculate(
                                                        formula,
                                                        scope
                                                    );
                                                }
                                            }

                                            if (isEnabled) {
                                                let max: number;
                                                let min: number;

                                                if (validation?.maximum) {
                                                    max = Excel.calculate(
                                                        String(
                                                            validation.maximum
                                                        ),
                                                        scope
                                                    );
                                                }

                                                if (validation?.minimum) {
                                                    min = Excel.calculate(
                                                        String(
                                                            validation.minimum
                                                        ),
                                                        scope
                                                    );
                                                }

                                                if (
                                                    max > -1 &&
                                                    position > max
                                                ) {
                                                    return false;
                                                }

                                                if (
                                                    min > -1 &&
                                                    position < min
                                                ) {
                                                    return false;
                                                }
                                            }

                                            return true;
                                        }
                                    );

                                    return tests.every(Boolean);
                                } catch (e) {
                                    return false;
                                }
                            }
                        ),
                })
            ),
        });
    }
};

export const getIsInnerDrawer = (productConfig: Structure[]) => {
    let isInnerDrawer = false;
    if (productConfig) {
        for (const config of productConfig) {
            if (config.name == 'Drawers') {
                if (config?.fieldsets?.length) {
                    for (const fieldset of config.fieldsets) {
                        if (fieldset.name == 'drawer_runners') {
                            if (fieldset?.options?.innerDrawer) {
                                isInnerDrawer = true;
                                break;
                            }
                        } else {
                            continue;
                        }
                    }
                }
            } else continue;

            if (isInnerDrawer) break;
        }
    }

    return isInnerDrawer;
};

export const getFormSchema = (
    productDetails: ProductForm,
    allFields: string[]
) => {
    let formSchema = object();
    const configIterator = productConfigIterator(productDetails.structure);

    let config;
    while ((config = configIterator.next())) {
        if (typeof config.value === 'undefined') break;

        const {field, fieldset} = config.value;

        const rule = validator(field, fieldset, allFields);
        const fieldName = field['name'];
        if (rule)
            formSchema = formSchema.shape({
                [fieldName]: rule,
            });
    }

    // properly check for inner darawer
    if (
        getIsInnerDrawer(productDetails.structure) &&
        productDetails.validation?.length
    ) {
        formSchema = getInnerDrawerValidationRules(
            productDetails.validation,
            formSchema
        );
    }

    formSchema = formSchema.shape({
        custom_colour_ext: yupString().when(
            'exterior_colour_name',
            ([exteriorColourName], schema) => {
                if (
                    String(exteriorColourName)
                        .toLowerCase()
                        .indexOf('custom colour') > -1
                ) {
                    return schema.test(
                        'requiredExteriorCustomColour',
                        'Please enter custom exterior colour name',
                        (colour) => colour && colour?.trim() != ''
                    );
                }
            }
        ),
        custom_colour_carc: yupString().when(
            'carcase_colour_name',
            ([carcaseColourName], schema) => {
                if (
                    String(carcaseColourName)
                        .toLowerCase()
                        .indexOf('custom colour') > -1
                ) {
                    return schema.test(
                        'requiredCarcaseCustomColour',
                        'Please enter custom carcase colour name',
                        (colour) => colour && colour?.trim() != ''
                    );
                }
            }
        ),
    });

    return formSchema;
};
